import * as React from 'react';
import tw from 'twin.macro';
import { ArrowPathIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { customImageLoader, getAssetSrc, useResponsiveBreakPoints } from '@restworld/utils-common';

type ServiceBoxVariant = 'job-preview' | 'publishing' | 'candidate-screening' | 'dashboard';

interface ServiceDescriptionBoxProps {
  variant: ServiceBoxVariant;
  title: string;
  description: string;
  cardPosition?: 'left' | 'right';
}

const Wrapper = tw.div`
  flex flex-col md:flex-row
  w-full
  p-4 md:(px-10 py-6)
  items-center
  gap-8
  rounded-2xl
  bg-[rgba(102,51,187,0.05)]
`;

const CardWrapper = tw.div`
  flex
  w-full md:w-[288px]
  p-5
  flex-col
  items-start
  gap-8
  rounded-2xl
  border border-neutral-40
  bg-neutral-20
  shrink-0
`;

const Description = tw.p`
  text-neutral-700
  text-base
  leading-[22px]
  max-w-[320px]
  self-center md:self-start
`;

const TitleWrapper = tw.h3`
  text-neutral-700
  text-xl
  font-semibold
  leading-normal
  self-center md:self-start
`;

const PurpleText = tw.span`text-purple-700`;

// Helper function to parse title with purple text markers
const parseTitle = (title: string) => {
  const parts = title.split(/(\{\{purple\}\}.*?\{\{\/purple\}\})/g);
  return parts.map((part, index) => {
    const purpleMatch = part.match(/\{\{purple\}\}(.*?)\{\{\/purple\}\}/);
    if (purpleMatch) {
      return <PurpleText key={index}>{purpleMatch[1]}</PurpleText>;
    }
    return <span key={index}>{part}</span>;
  });
};

const JobPreviewCard = () => {
  const { t } = useTranslation('product-description-page');

  return (
    <CardWrapper>
      {/* Header with job title and location */}
      <div tw="flex justify-center items-center gap-4 w-full">
        <Image
          width={32}
          height={32}
          loader={customImageLoader}
          tw="rounded-full"
          src="https://api.builder.io/api/v1/image/assets/TEMP/95382ceb98f0a527f63f21b92fb09b5b724f840c?width=64"
          alt="Company logo"
        />
        <div tw="flex flex-col items-start flex-1">
          <div tw="text-neutral-700 font-semibold text-base">Cuoco Capo Partita</div>
          <div tw="text-neutral-500 text-sm">Ristorante Silos, Torino, Italia</div>
        </div>
      </div>

      {/* Skills section */}
      <div tw="flex flex-col items-start gap-4 w-full">
        <div tw="flex flex-col items-start gap-1 w-full">
          <div tw="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" tw="shrink-0">
              <path d="M8 2L10.5 7L16 7.75L12 11.5L13 16L8 13.25L3 16L4 11.5L0 7.75L5.5 7L8 2Z" fill="#666676" />
            </svg>
            <div tw="text-neutral-500 text-sm font-medium">Competenze richieste</div>
          </div>
          <div tw="w-full max-w-[259px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[199px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[223px] h-2 rounded bg-neutral-50"></div>
        </div>

        {/* Hours section */}
        <div tw="flex flex-col items-start gap-1 w-full">
          <div tw="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" tw="shrink-0">
              <circle cx="8" cy="8" r="7" fill="#666676" />
              <path d="M8 4v4l3 2" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <div tw="text-neutral-500 text-sm font-medium">Orari</div>
          </div>
          <div tw="w-full max-w-[219px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[200px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[259px] h-2 rounded bg-neutral-50"></div>
        </div>

        {/* Contract section */}
        <div tw="flex flex-col items-start gap-1 w-full">
          <div tw="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" tw="shrink-0">
              <rect x="3" y="2" width="10" height="12" rx="1" fill="#666676" />
              <path d="M6 6h4M6 9h3" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
            </svg>
            <div tw="text-neutral-500 text-sm font-medium">Contratto</div>
          </div>
          <div tw="w-full max-w-[128px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[248px] h-2 rounded bg-neutral-50"></div>
          <div tw="w-full max-w-[177px] h-2 rounded bg-neutral-50"></div>
        </div>
      </div>
    </CardWrapper>
  );
};

const PublishingCard = () => {
  return (
    <CardWrapper tw="justify-center items-center">
      <div tw="relative w-full max-w-[266px] h-[160px]">
        <Image
          fill
          loader={customImageLoader}
          src="https://api.builder.io/api/v1/image/assets/TEMP/23b3919e6365531a47f91217ca4ca0076ddd56de?width=533"
          alt="Publishing platforms"
          tw="object-contain"
        />
      </div>
      <button
        tw="
          flex h-8 justify-center items-center 
          px-4 
          rounded-md 
          bg-purple-50 
          text-purple-800
          text-sm font-medium
          shadow-md
          pointer-events-none
        "
      >
        Pubblica annuncio
      </button>
    </CardWrapper>
  );
};

const CandidateScreeningCard = () => {
  return (
    <CardWrapper>
      {/* Candidate header */}
      <div tw="flex items-center gap-3 w-full pt-4">
        <div tw="flex items-center gap-3 rounded-lg border border-neutral-40">
          <Image
            width={64}
            height={64}
            loader={customImageLoader}
            tw="rounded-lg object-cover"
            src="https://api.builder.io/api/v1/image/assets/TEMP/abc4979a81f59610caeeb39be638ed26e4b62039?width=128"
            alt="Giovanni Johanssen"
          />
        </div>
        <div tw="flex flex-col items-start gap-0.5">
          <div tw="text-neutral-700 font-semibold text-sm truncate max-w-[128px]">Giovanni</div>
          <div tw="text-neutral-700 font-semibold text-sm truncate max-w-[128px]">Johanssen</div>
        </div>
      </div>

      {/* Status items */}
      <div tw="flex flex-col gap-3 w-full">
        <div tw="flex justify-between items-center w-full">
          <div tw="text-neutral-700 text-sm flex-1">Screening Whatsapp</div>
          <ArrowPathIcon tw="w-4 h-4 text-neutral-700" />
        </div>
        <div tw="flex justify-between items-center w-full">
          <div tw="text-neutral-700 text-sm flex-1">Parsing CV</div>
          <CheckCircleIcon tw="w-4 h-4 text-green-700" />
        </div>
        <div tw="flex justify-between items-center w-full">
          <div tw="text-neutral-700 text-sm flex-1">Interessi e ricerche Restworld</div>
          <CheckCircleIcon tw="w-4 h-4 text-green-700" />
        </div>
      </div>
    </CardWrapper>
  );
};

const DashboardCard = () => {
  return (
    <Image
      src={getAssetSrc('/utils-assets/employer-app-mockup.png')}
      alt="Dashboard preview"
      width={420}
      height={230}
      loader={customImageLoader}
    />
    // <img
    //   tw="
    //     w-full md:w-[288px]
    //     h-auto
    //     aspect-[144/115]
    //     rounded-2xl
    //     border border-neutral-40
    //     object-cover
    //     shrink-0
    //   "
    //   src="https://api.builder.io/api/v1/image/assets/TEMP/e689ffc3e6ec4e3f4d808540724d2ea343dcee78?width=576"
    //   alt="Dashboard preview"
    // />
  );
};

export const ServiceDescriptionBox: React.FC<ServiceDescriptionBoxProps> = ({
  variant,
  title,
  description,
  cardPosition,
}) => {
  const { t } = useTranslation('product-description-page');
  const { isMobile } = useResponsiveBreakPoints();

  const renderCard = () => {
    switch (variant) {
      case 'job-preview':
        return <JobPreviewCard />;
      case 'publishing':
        return <PublishingCard />;
      case 'candidate-screening':
        return <CandidateScreeningCard />;
      case 'dashboard':
        return <DashboardCard />;
      default:
        return null;
    }
  };

  const Card = () => <div tw="flex flex-row justify-center">{renderCard()}</div>;

  return (
    <Wrapper>
      <div tw="grid grid-cols-1 md:grid-cols-2 justify-center gap-8 w-full">
        {cardPosition === 'left' && !isMobile && <Card />}
        <div tw="flex flex-col justify-center items-start text-center md:text-left gap-6 flex-1 mx-4 md:mx-10 lg:mx-20">
          <TitleWrapper>{parseTitle(title)}</TitleWrapper>
          {variant === 'dashboard' ? (
            <>
              <Description>{t('how_it_works_step_4_intro')}</Description>
              <ul tw="list-disc list-inside text-neutral-700 text-base leading-[22px] max-w-[320px]">
                <li>{t('how_it_works_step_4_item_1')}</li>
                <li>{t('how_it_works_step_4_item_2')}</li>
                <li>{t('how_it_works_step_4_item_3')}</li>
              </ul>
              <Description>{t('how_it_works_step_4_outro')}</Description>
            </>
          ) : variant === 'candidate-screening' ? (
            <>
              <Description>{t('how_it_works_step_3_intro')}</Description>
              <Description>{t('how_it_works_step_3_outro')}</Description>
            </>
          ) : (
            (() => {
              if (typeof description === 'string' && description.includes('\n\n')) {
                const parts = description.split('\n\n');
                return (
                  <>
                    <Description>{parts[0]}</Description>
                    <Description>{parts.slice(1).join('\n\n')}</Description>
                  </>
                );
              }
              return <Description>{description}</Description>;
            })()
          )}
        </div>
        {(cardPosition === 'right' || isMobile) && <Card />}
      </div>
    </Wrapper>
  );
};
